import { gql } from '@apollo/client'
import type { SendMetrics } from '@jusbrasil-web/shared-utils-events'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'

import {
  ValidateOabModalProvider_OabTransferringFragment,
  ValidateOabModalProvider_ViewerFragment,
} from './__generated__/context.graphql'
import { ERROR_MESSAGES, ErrorMessageKey, selectErrorKey } from './error-messages'
import { useOabTransferringEmailJwt } from './hooks'
import { ModalStep, stepTransitions } from './steps'

export type FormFields = {
  cpf: string
  birthDate: string
  phone: string
  oab: string
  oabUf: string
  oabUfLabel: string
  oabType: string
  origin: string | null
}

type FormFieldsServer = Omit<FormFields, 'birthDate' | 'oabUf'> & {
  birthDate: number
  oabUf: number
  phoneArea: string
  phoneNumber: string
}

export type Form = UseFormReturn<FormFields>

interface ValidateOabModalContextBase {
  initialized: boolean
  form: Form | null
  open: boolean
  setOpen(open: boolean): void
  step: ModalStep
  goNextSuccessStep(): void
  goNextErrorStep(errorKey: string): void
  resetToInitialStep(): void
  setOpenToFalse(): void
  errorKey: ErrorMessageKey | null
  errorMessage: string
  getServerDataValues(): FormFieldsServer
  emailConflicted: string
  setEmailConflicted(email: string): void
  setReloadOnModalClose(reload: boolean): void
}

export interface ValidateOabModalContextEmpty extends ValidateOabModalContextBase {
  initialized: false
  form: null
}

export interface ValidateOabModalContextInitialized extends ValidateOabModalContextBase {
  initialized: true
  form: Form
}

export interface ValidateOabModalProviderProps {
  children(value: ValidateOabModalContextInitialized): ReactNode
  setContext(context: ValidateOabModalContextInitialized): void
  oabTransferring: ValidateOabModalProvider_OabTransferringFragment | null
  viewer: ValidateOabModalProvider_ViewerFragment
  origin?: string
  sendMetrics: SendMetrics
}

function defaultFunction(): void {
  // istanbul ignore next
  if (process.env.NODE_ENV === 'development') {
    console.warn('Function not implemented.')
  }
}

const ValidateOabModalContext = createContext<
  ValidateOabModalContextEmpty | ValidateOabModalContextInitialized
>({
  open: false,
  setOpen: defaultFunction,
  step: 'confirmId',
  initialized: false,
  goNextSuccessStep: defaultFunction,
  goNextErrorStep: defaultFunction,
  resetToInitialStep: defaultFunction,
  setOpenToFalse: defaultFunction,
  form: null,
  errorKey: null,
  errorMessage: '',
  getServerDataValues: () => {
    // istanbul ignore next
    throw new Error('Function not implemented.')
  },
  emailConflicted: '',
  setEmailConflicted: defaultFunction,
  setReloadOnModalClose: defaultFunction,
})

const dateTimeFormat = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

export function ValidateOabModalProvider(props: ValidateOabModalProviderProps) {
  const { children, setContext, oabTransferring, viewer, sendMetrics } = props
  const [step, setStep] = useState<ModalStep>('confirmId')
  const [open, setOpen] = useState(false)
  const [errorKey, setErrorKey] = useState<ErrorMessageKey | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [emailConflicted, setEmailConflicted] = useState('')
  const [reloadOnModalClose, setReloadOnModalClose] = useState(false)
  const openedOnceRef = useRef(false)

  const { cpf, birthDate, oab, oabUf, oabType, oabUfLabel, origin } = oabTransferring || {
    ...viewer,
    oabUf: viewer.oabUf?.tid,
    oabUfLabel: viewer.oabUf?.directoryUrl?.toUpperCase(),
    origin: props.origin,
  }
  const { pid, phones, isOabValid } = viewer

  const form: Form = useForm({
    mode: 'onTouched',
    defaultValues: {
      cpf: cpf ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '',
      birthDate: birthDate ? dateTimeFormat.format(new Date(birthDate)) : '',
      phone: phones?.[0]?.brFormat || '',
      oab: oab ? oab.replace(/^0+|\D+/g, '') : '',
      oabUf: oabUf ? `${oabUf}` : '',
      oabUfLabel: oabUfLabel || '',
      oabType: oabType || '',
      origin: origin || null,
    },
  })

  useEffect(() => {
    if (open) {
      openedOnceRef.current = true
    } else if (reloadOnModalClose) {
      window.location.reload()
    }
  }, [open, reloadOnModalClose])

  const getServerDataValues = useCallback(() => {
    const removeNonDigits = (value: string) => value.replace(/\D/g, '')
    const convertToTime = (value: string): number => {
      const [day, month, year] = value.split('/')
      return new Date(`${year}-${month}-${day}T00:00:00-03:00`).getTime()
    }

    const values = form.getValues()
    const phoneNumbers = removeNonDigits(values.phone)

    const normalized: FormFieldsServer = {
      ...values,
      phoneArea: phoneNumbers.slice(0, 2),
      phoneNumber: phoneNumbers.slice(2),
      cpf: removeNonDigits(values.cpf),
      birthDate: convertToTime(values.birthDate),
      oabUf: parseInt(values.oabUf, 10),
    }

    return normalized
  }, [form])

  const goNextSuccessStep = useCallback(() => {
    const nextStep = stepTransitions[step]?.success
    if (nextStep) {
      setStep(nextStep)
    } else {
      console.warn(`Step ${step} has no success transition`)
    }
  }, [step])

  const goNextErrorStep = useCallback(
    (errorKeyNext: string) => {
      const nextStep = stepTransitions[step]?.error
      if (nextStep) {
        const errorKeyHandled = selectErrorKey(errorKeyNext)
        setErrorKey(errorKeyHandled)

        const errorMessage =
          errorKeyHandled === 'DUPLICATED_VALIDATION_ERROR'
            ? ERROR_MESSAGES[errorKeyHandled].replace(
                '%s',
                form.getValues(['oab', 'oabUfLabel']).join('/')
              )
            : ERROR_MESSAGES[errorKeyHandled]

        setErrorMessage(errorMessage)
        setStep(nextStep)
      } else {
        console.warn(`Step ${step} has no error transition`)
      }
    },
    [form, step]
  )

  const { emailJwt } = useOabTransferringEmailJwt()

  useEffect(() => {
    if (emailJwt) {
      setStep('oabTransferring')
      setOpen(true)
    }
  }, [emailJwt])

  const resetToInitialStep = useCallback(() => {
    setStep('confirmId')
    setErrorKey(null)
    setErrorMessage('')
  }, [])

  const setOpenToFalse = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!openedOnceRef.current) {
      return
    }

    const properties = { step }
    if (open) {
      sendMetrics('UserAccount.OABValidation', `${pid}`, properties)
    } else {
      sendMetrics('UserAccount.OABValidation.ModalClosed', `${pid}`, properties)
    }
  }, [open, pid, sendMetrics, step])

  useEffect(() => {
    const metadata = { isOabValid, oab, oabType, oabUf }
    const properties = { step, type: errorKey, metadata }
    if (step === 'validatingSuccess') {
      sendMetrics('UserAccount.OABValidationSuccess', `${pid}`, properties)
    } else if (step === 'validatingError') {
      sendMetrics('UserAccount.OABValidationFailed', `${pid}`, properties)
    }
  }, [errorKey, isOabValid, oab, oabType, oabUf, pid, sendMetrics, step])

  const context: ValidateOabModalContextInitialized = {
    open,
    setOpen,
    step,
    initialized: true,
    goNextSuccessStep,
    goNextErrorStep,
    resetToInitialStep,
    setOpenToFalse,
    form,
    errorKey,
    errorMessage,
    getServerDataValues,
    emailConflicted,
    setEmailConflicted,
    setReloadOnModalClose,
  }

  setContext(context)

  return (
    <ValidateOabModalContext.Provider value={context}>
      {children(context)}
    </ValidateOabModalContext.Provider>
  )
}

ValidateOabModalProvider.fragments = {
  viewer: gql`
    fragment ValidateOabModalProvider_viewer on Profile {
      pid
      isOabValid
      phones {
        brFormat
      }
      cpf
      birthDate
      oab
      oabUf {
        tid
        directoryUrl
      }
      oabType
    }
  `,

  oabTransferring: gql`
    fragment ValidateOabModalProvider_oabTransferring on OabTransferring {
      cpf
      birthDate
      oab
      oabUf
      oabUfLabel
      oabType
      origin
    }
  `,
}

export function useValidateOabModalContextEmpty(): ValidateOabModalContextEmpty {
  const context = useContext(ValidateOabModalContext)

  if (context.initialized) {
    throw new Error(
      'useValidateOabModalContextEmpty must be used outside a ValidateOabModalProvider'
    )
  }

  return context
}

export function useValidateOabModalContext(): ValidateOabModalContextInitialized {
  const context = useContext(ValidateOabModalContext)

  if (!context.initialized) {
    throw new Error('useValidateOabModalContext must be used within a ValidateOabModalProvider')
  }

  return context
}
